/**
 * QC-REVIEW-UI-COMPACT-002 (Oct 2026) — QC Timeline & Notes drawer.
 *
 * Read-only assembly of EXISTING data (nothing is fabricated):
 *   • qc_history[]           — submission notes, QC return reasons / approve notes   (Notes)
 *   • GET /qc-notes          — internal QC notes (qc.note_added activity events)      (Notes)
 *   • typing_return_reason   — why Typing sent the order back                         (Notes)
 *   • GET /activity          — qc.* / typing.* workflow events                        (System)
 *   • tax_sheet_review       — self-review status                                     (System)
 * Composer → POST /orders/{id}/qc-notes (append-only, never mutates the order).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, History, MessageSquare, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const MAX_NOTE = 2000;

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return isNaN(d) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const initials = (n) => (n || '?').split(' ').filter(Boolean).map((s) => s[0]).join('').slice(0, 2).toUpperCase();

/* ── Build the unified entry list from existing data ───────── */
export const buildQcTimeline = ({ order, qcHistory, qcNotes, events }) => {
  const out = [];
  const push = (e) => e.at && out.push(e);
  const o = order || {};

  (qcHistory || []).forEach((h) => {
    if (h.submitted_notes?.trim()) {
      push({ id: `sub-${h.id}`, kind: 'note', tone: 'processor', title: `Note from ${h.submitted_by_name || 'Processor'}`, by: h.submitted_by_name,
        at: h.submitted_at, text: h.submitted_notes, status: `Iteration ${h.iteration}`, badge: 'Note' });
    }
    if (h.decision === 'returned' && (h.rejection_reason || h.notes)) {
      push({ id: `ret-${h.id}`, kind: 'note', tone: 'return', title: `Returned for correction${h.decided_by_name ? ` by ${h.decided_by_name}` : ''}`, by: h.decided_by_name,
        at: h.decided_at, text: h.rejection_reason || h.notes, status: [h.rejection_category, h.severity].filter(Boolean).join(' · '), badge: 'Return' });
    }
    if (h.decision === 'approved' && h.notes?.trim()) {
      push({ id: `apr-${h.id}`, kind: 'note', tone: 'approve', title: `Approval note${h.decided_by_name ? ` from ${h.decided_by_name}` : ''}`, by: h.decided_by_name,
        at: h.decided_at, text: h.notes, status: 'Approved', badge: 'Approval' });
    }
  });
  // Current submission note when not already captured by qc_history
  if (o.qc_notes?.trim() && !(qcHistory || []).some((h) => (h.submitted_notes || '').trim() === o.qc_notes.trim())) {
    push({ id: 'qc-notes-current', kind: 'note', tone: 'processor', title: `Note from ${o.qc_submitted_by_name || 'Processor'}`, by: o.qc_submitted_by_name,
      at: o.qc_submitted_at, text: o.qc_notes, status: 'Submission', badge: 'Note' });
  }
  if (o.typing_status === 'returned_to_qc' && o.typing_return_reason) {
    push({ id: 'typing-return', kind: 'note', tone: 'return', title: `Returned from Typing${o.typing_returned_by_name ? ` by ${o.typing_returned_by_name}` : ''}`, by: o.typing_returned_by_name || 'Typing Processor',
      at: o.typing_returned_at || o.qc_reentered_at, text: o.typing_return_reason, status: 'Returned · Typing', badge: 'Return' });
  }
  (qcNotes || []).forEach((n) => push({ id: `qcn-${n.id}`, kind: 'note', tone: 'qc', title: `QC note from ${n.author_name || 'QC'}`, by: n.author_name,
    at: n.created_at, text: n.note, status: n.author_role?.replace(/_/g, ' '), badge: 'QC Note' }));

  (events || []).forEach((ev) => {
    const t = ev.type || '';
    if (t === 'qc.note_added') return;                         // shown under Notes, never System
    if (!/^(qc|typing)\./.test(t)) return;
    push({ id: `ev-${ev.id}`, kind: 'system', tone: t.includes('approved') ? 'approve' : t.includes('return') || t.includes('reject') ? 'return' : 'system',
      title: ev.summary, by: ev.actor?.name || ev.actor_name || 'System', at: ev.created_at, text: ev.details?.notes || ev.details?.rejection_reason || '',
      status: t.replace(/^(qc|typing)\./, '').replace(/_/g, ' '), badge: 'System' });
  });
  const tsr = o.tax_sheet_review || {};
  if (tsr.status === 'reviewed' && tsr.reviewed_at) {
    push({ id: 'self-review', kind: 'system', tone: 'approve', title: `Self-review completed${tsr.reviewed_by_name ? ` by ${tsr.reviewed_by_name}` : ''}`, by: tsr.reviewed_by_name,
      at: tsr.reviewed_at, text: '', status: 'Reviewed', badge: 'System' });
  } else if (o.qc_submitted_at) {
    push({ id: 'self-review-pending', kind: 'system', tone: 'pending', title: 'Self-review pending', by: 'System', at: o.qc_submitted_at,
      text: 'Order submitted for QC review without a completed self-review.', status: 'Pending', badge: 'System' });
  }
  return out.sort((a, b) => new Date(b.at) - new Date(a.at));
};

/* ── Data hook (shared by bar count + drawer) ─────────────── */
export const useQcTimeline = (order) => {
  const [state, setState] = useState({ qcHistory: [], qcNotes: [], events: [], loading: true });
  const load = useCallback(async () => {
    if (!order?.id) return;
    const get = (p) => fetch(`${API}/orders/${order.id}${p}`, { headers: authHeaders() }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const [h, n, a] = await Promise.all([get('/qc-history'), get('/qc-notes'), get('/activity?limit=100')]);
    setState({ qcHistory: h?.history || [], qcNotes: n?.notes || [], events: a?.events || [], loading: false });
  }, [order?.id]);
  useEffect(() => { load(); }, [load]);
  const entries = useMemo(() => buildQcTimeline({ order, ...state }), [order, state]);
  const addNote = useCallback(async (text) => {
    const res = await fetch(`${API}/orders/${order.id}/qc-notes`, {
      method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ note: text }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.detail?.[0]?.msg || body.detail || 'Could not add note');
    setState((s) => ({ ...s, qcNotes: [...s.qcNotes, body.note] }));
    return body.note;
  }, [order?.id]);
  return { entries, loading: state.loading, addNote, reload: load };
};

/* ── Presentation ─────────────────────────────────────────── */
const TONE = {
  processor: { dot: 'bg-blue-500', avatar: 'bg-blue-100 text-blue-700', card: 'bg-white border-slate-200', badge: 'bg-blue-50 text-blue-700' },
  qc:        { dot: 'bg-violet-500', avatar: 'bg-violet-100 text-violet-700', card: 'bg-violet-50/40 border-violet-200', badge: 'bg-violet-100 text-violet-700' },
  return:    { dot: 'bg-orange-500', avatar: 'bg-orange-100 text-orange-700', card: 'bg-orange-50/50 border-orange-200', badge: 'bg-orange-100 text-orange-700' },
  approve:   { dot: 'bg-emerald-500', avatar: 'bg-emerald-100 text-emerald-700', card: 'bg-emerald-50/50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-700' },
  pending:   { dot: 'bg-amber-400', avatar: 'bg-amber-100 text-amber-700', card: 'bg-amber-50/60 border-amber-200', badge: 'bg-amber-100 text-amber-800' },
  system:    { dot: 'bg-slate-400', avatar: 'bg-slate-100 text-slate-600', card: 'bg-slate-50 border-slate-200', badge: 'bg-slate-200 text-slate-700' },
};

const Entry = ({ e }) => {
  const t = TONE[e.tone] || TONE.system;
  return (
    <li className="relative pl-7" data-testid={`qc-timeline-entry-${e.id}`} data-kind={e.kind}>
      <span className={`absolute left-[7px] top-4 h-2.5 w-2.5 rounded-full ring-4 ring-white ${t.dot}`} aria-hidden="true" />
      <div className={`rounded-xl border p-3.5 transition-[box-shadow,transform] duration-200 hover:shadow-md ${t.card}`}>
        <div className="flex items-start gap-3">
          <span className={`h-9 w-9 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${t.avatar}`}>
            {e.kind === 'system' && e.by === 'System' ? 'SYS' : initials(e.by)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-[13px] font-semibold text-slate-900 leading-snug">{e.title}</p>
              <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${t.badge}`}>{e.badge}</span>
            </div>
            <p className="text-[11.5px] text-slate-500 mt-0.5">{e.by && e.by !== 'System' ? `${e.by} · ` : 'System · '}{fmt(e.at)}{e.status ? ` · ${e.status}` : ''}</p>
            {e.text && <p className="text-[13px] text-slate-700 mt-2 whitespace-pre-wrap break-words leading-relaxed">{e.text}</p>}
          </div>
        </div>
      </div>
    </li>
  );
};

const QCNotesDrawer = ({ open, onClose, orderNumber, entries, loading, canAddNote, onAddNote }) => {
  const [tab, setTab] = useState('all');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setTab('all'); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev) => ev.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const notes = entries.filter((e) => e.kind === 'note');
  const system = entries.filter((e) => e.kind === 'system');
  const shown = tab === 'notes' ? notes : tab === 'system' ? system : entries;
  const trimmed = text.trim();

  const submit = async () => {
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      await onAddNote(trimmed);
      setText('');
      setTab((t) => (t === 'system' ? 'all' : t));
      toast.success('QC note added');
    } catch (err) {
      toast.error(err.message || 'Could not add note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className={`fixed inset-0 z-[70] bg-slate-900/20 backdrop-blur-[1px] transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose} aria-hidden="true" data-testid="qc-notes-drawer-backdrop" />
      <aside role="dialog" aria-modal="true" aria-label="QC Timeline & Notes" aria-hidden={!open}
        className={`fixed top-0 right-0 z-[71] h-full w-full sm:w-[460px] max-w-full bg-white shadow-2xl border-l border-slate-200 flex flex-col
          transition-transform duration-300 ease-[cubic-bezier(.22,.61,.36,1)] ${open ? 'translate-x-0' : 'translate-x-full'}`}
        data-testid="qc-notes-drawer" data-open={open ? 'true' : 'false'}>
        <header className="flex items-center justify-between gap-3 px-5 pt-5 pb-3">
          <h2 className="flex items-center gap-2 text-[16px] font-bold text-slate-900">
            <History className="h-5 w-5 text-[#004aad]" aria-hidden="true" />
            QC Timeline &amp; Notes <span className="text-slate-400 font-semibold" data-testid="qc-notes-drawer-count">({entries.length})</span>
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="qc-notes-drawer-close"
            className="h-8 w-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center transition-colors duration-150">
            <X className="h-4 w-4" />
          </button>
        </header>
        {orderNumber && <p className="px-5 -mt-1 text-[12px] text-slate-500">{orderNumber}</p>}

        <div className="px-5 mt-3 border-b border-slate-200 flex gap-1" role="tablist" data-testid="qc-notes-tabs">
          {[['all', 'All', entries.length], ['notes', 'Notes', notes.length], ['system', 'System', system.length]].map(([id, label, n]) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`qc-notes-tab-${id}`}
              className={`relative -mb-px px-3.5 py-2.5 text-[13px] font-semibold transition-colors duration-200 border-b-2 ${
                tab === id ? 'text-[#004aad] border-[#004aad]' : 'text-slate-500 border-transparent hover:text-slate-800'}`}>
              {label} <span className="text-slate-400 font-medium">({n})</span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="qc-notes-list">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : shown.length === 0 ? (
            <div className="text-center py-14" data-testid="qc-notes-empty">
              <MessageSquare className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-[13px] font-medium text-slate-600">{tab === 'notes' ? 'No notes yet' : tab === 'system' ? 'No system events' : 'Nothing recorded yet'}</p>
              {canAddNote && tab !== 'system' && <p className="text-[12px] text-slate-400 mt-1">Add the first internal QC note below.</p>}
            </div>
          ) : (
            <ol key={tab} className="relative space-y-3 before:absolute before:left-[11px] before:top-3 before:bottom-3 before:w-px before:bg-slate-200 animate-in fade-in-0 duration-200">
              {shown.map((e) => <Entry key={e.id} e={e} />)}
            </ol>
          )}
        </div>

        {canAddNote && (
          <footer className="border-t border-slate-200 bg-slate-50/70 px-5 py-4" data-testid="qc-add-note-form">
            <div className="flex items-start gap-2.5">
              <span className="h-9 w-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center flex-shrink-0 mt-0.5"><MessageSquare className="h-4 w-4" /></span>
              <div className="flex-1 min-w-0">
                <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, MAX_NOTE))} rows={2} maxLength={MAX_NOTE}
                  placeholder="Add a note…" data-testid="qc-add-note-input"
                  onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
                  className="w-full resize-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[13px] text-slate-800 placeholder:text-slate-400 shadow-sm
                    focus:outline-none focus:border-[#004aad] focus:ring-2 focus:ring-blue-100 transition-[border-color,box-shadow] duration-200" />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[11px] text-slate-400" data-testid="qc-add-note-counter">Internal · {text.length}/{MAX_NOTE}</span>
                  <button type="button" onClick={submit} disabled={!trimmed || saving} data-testid="qc-add-note-submit"
                    className="h-9 px-4 inline-flex items-center gap-1.5 rounded-lg bg-[#004aad] text-white text-[13px] font-semibold shadow-sm
                      transition-[background-color,box-shadow,transform] duration-200 hover:bg-[#003c8f] hover:shadow hover:-translate-y-px
                      disabled:opacity-50 disabled:pointer-events-none">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Add Note
                  </button>
                </div>
              </div>
            </div>
          </footer>
        )}
      </aside>
    </>
  );
};

export default QCNotesDrawer;
